import {utils} from '@wallaby-ui/core';
import {ColDef} from 'ag-grid-community';
import {cloneDeep, filter, find, forEach, isEmpty, includes} from 'lodash';
import {constants} from 'src/js/common';
import {StaticComponent} from 'src/js/model/static_components_definitions';

import {ColumnConfig, TABLE_TYPE_AG, TableType} from './column_config';
import {CONFIGURATION_VIEWS_DEFAULT} from './document_config';
import {FilterButtonConfig} from './filter_button_config';
import {SearchTypeViews} from './search_type_views';

export const ROW_IDX = '$rowIdx';

export const RowAction = {
  ADD: 'ADD',
  REMOVE: 'REMOVE',
  UPDATE: 'UPDATE',
};

export const TABLE_ROW_MAX_SUB_ROWS = 'maxSubRows';
const TABLE_COLUMNS = 'columns';
export const TABLE_CONFIG = 'config';
const TABLE_INITIAL_SORT = 'initialSort';
const TABLE_LAYOUT = 'layout';
const TABLE_OPTIONS = 'options';
const TABLE_DEFAULT_PAGINATION = 'defaultPagination';
const TABLE_TITLE_COLUMN = 'titleColumn';
const TABLE_VIEWS = 'views';
const TABLE_COLLECTION_PROPERTY = 'collectionProperty';
const FILTER_BUTTONS = 'filterButtons';
const LEGEND = 'legend';
const ADDITIONAL_SORT_MODEL = 'additionalSortModel';

const GRID_OPTIONS_GRID = 'grid';
const GRID_OPTIONS_DOM_LAYOUT = 'domLayout';
const GRID_OPTIONS_DOM_LAYOUT_AUTO_HEIGHT = 'autoHeight';
/** Redraw cell renderers on sort */
export const GRID_OPTIONS_REDRAW_ON_SORT = 'redrawOnSort';
const GRID_OPTIONS_SELECTED_ROW_ID_DATA_PATH = 'selectedRowIdDataPath';

const logger = utils.LogUtils.getLogger('TableConfig', constants.LIBRARY_NAME);

export interface TableConfigRaw {
  columns: ColDef[];
}

export class TableConfig {
  private readonly rawData: Partial<TableConfigRaw>;

  private readonly id: number | undefined;

  private readonly tableType: any;

  private readonly columns: ColumnConfig[];

  private readonly dataGridOptions: any;

  private readonly filterButtons: any[];

  private readonly validFilterButtons: any[];

  private readonly views: SearchTypeViews | undefined;

  private readonly layout: any;

  private readonly loaded: boolean;

  private readonly legend: any | undefined;

  static initColumns(
    rawColumns: ColDef[],
    tableType: TableType = TABLE_TYPE_AG
  ) {
    const columns: ColumnConfig[] = [];
    if (rawColumns) {
      forEach(rawColumns, (column, idx) => {
        columns.push(new ColumnConfig(column, idx, tableType));
      });
    }
    return columns;
  }

  static initFilterButtons(rawButtons) {
    const filterButtons: FilterButtonConfig[] = [];
    if (rawButtons) {
      forEach(rawButtons, (button, idx) => {
        filterButtons.push(new FilterButtonConfig(button, idx));
      });
    }
    return filterButtons;
  }

  static initValidFilterButtons(filterButtons) {
    const validButtons = filter(filterButtons, (filterButton) => {
      return filterButton.isValid();
    });
    if (validButtons.length !== filterButtons.length) {
      logger.warn(
        `Filter buttons configuration contains invalid buttons: ${filterButtons}`
      );
    }
    return validButtons;
  }

  // eslint-disable-next-line consistent-return
  static initLegend(rawLegend) {
    if (StaticComponent.getComponent(rawLegend)) {
      return StaticComponent.getComponent(rawLegend);
    }
  }

  static isAutoHeightDomLayout(dataGridOptions) {
    const domLayout =
      dataGridOptions &&
      dataGridOptions[GRID_OPTIONS_GRID] &&
      dataGridOptions[GRID_OPTIONS_GRID][GRID_OPTIONS_DOM_LAYOUT]
        ? dataGridOptions[GRID_OPTIONS_GRID][GRID_OPTIONS_DOM_LAYOUT]
        : undefined;
    return domLayout === GRID_OPTIONS_DOM_LAYOUT_AUTO_HEIGHT;
  }

  static filterColumnsByView(columns, views, viewId = 'document') {
    let columnsByView = columns;
    if (!isEmpty(views)) {
      if (!isEmpty(views.getView(viewId)) && views.getView(viewId).getItems()) {
        // get columns only from the wanted view
        const view = views.getView(viewId).getItems();
        columnsByView = filter(columns, (column) =>
          includes(view, column.getKey())
        );
        // if viewId not found in config, use default
      } else if (
        !isEmpty(views.getView(CONFIGURATION_VIEWS_DEFAULT)) &&
        views.getView(CONFIGURATION_VIEWS_DEFAULT).getItems()
      ) {
        const view = views.getView(CONFIGURATION_VIEWS_DEFAULT).getItems();
        columnsByView = filter(columns, (column) =>
          includes(view, column.getKey())
        );
      }
    }
    return columnsByView;
  }

  static withoutTitleColumn(columns, titleColumnId) {
    return titleColumnId
      ? filter(columns, (column) => column.getKey() !== titleColumnId)
      : columns;
  }

  constructor(
    rawData: Partial<TableConfigRaw> = {},
    id = undefined,
    tableType = TABLE_TYPE_AG
  ) {
    this.rawData = {...rawData};
    this.id = id;
    this.tableType = tableType;
    this.columns = [];
    this.dataGridOptions = {};
    this.filterButtons = [];
    this.validFilterButtons = [];
    this.layout = {};
    this.loaded = false;
    this.legend = undefined;

    if (this.rawData && this.rawData[TABLE_COLUMNS]) {
      this.columns = TableConfig.initColumns(
        this.rawData[TABLE_COLUMNS],
        this.tableType
      );
      this.views = new SearchTypeViews(this.rawData[TABLE_VIEWS], {
        columnsLength: this.columns.length,
        tableType: this.tableType,
      });
      this.dataGridOptions = {...this.rawData[TABLE_OPTIONS]};
      this.loaded = true;
    }
    if (this.rawData && this.rawData[FILTER_BUTTONS]) {
      this.filterButtons = TableConfig.initFilterButtons(
        this.rawData[FILTER_BUTTONS]
      );
      this.validFilterButtons = TableConfig.initValidFilterButtons(
        this.filterButtons
      );
    }
    if (this.rawData && this.rawData[TABLE_LAYOUT]) {
      this.layout = {...this.rawData[TABLE_LAYOUT]};
    }
    if (this.rawData && this.rawData[LEGEND]) {
      this.legend = TableConfig.initLegend(this.rawData[LEGEND]);
    }
  }

  clone() {
    return Object.assign(new TableConfig(cloneDeep(this.rawData)), this);
  }

  getNewRowInstance(viewId = 'document') {
    const newRow = {};
    this.getColumnsByView(viewId).forEach((column) => {
      newRow[column.getKey()] = '';
    });
    return newRow;
  }

  isLoaded() {
    return this.loaded;
  }

  getId() {
    return this.id;
  }

  /**
   * Tries to find a view in the views definition. If the view was not found, tries to
   * use default view. Otherwise, returns null.
   * @param viewId View Id to be found
   * @returns found view or null
   */
  findView(viewId = 'document'): SearchTypeViews | null {
    const views = this.getViews();
    let result = null;
    if (!isEmpty(views)) {
      if (!isEmpty(views.getView(viewId))) {
        result = views.getView(viewId);
      } else if (!isEmpty(views.getView(CONFIGURATION_VIEWS_DEFAULT))) {
        result = views.getView(CONFIGURATION_VIEWS_DEFAULT);
      }
    }
    return result;
  }

  getCollectionProperty(viewId = 'document') {
    const coreCollectionProperty =
      this.rawData && this.rawData[TABLE_COLLECTION_PROPERTY]
        ? this.rawData[TABLE_COLLECTION_PROPERTY]
        : undefined;

    const view = this.findView(viewId);
    if (
      !isEmpty(view) &&
      view.getCollectionProperty &&
      view.getCollectionProperty()
    ) {
      return view.getCollectionProperty();
    }
    return coreCollectionProperty;
  }

  getColumns(excludeTitleColumn = true) {
    return excludeTitleColumn
      ? TableConfig.withoutTitleColumn(this.columns, this.getTitleColumnId())
      : this.columns;
  }

  getInitialSort() {
    return this.rawData && this.rawData[TABLE_INITIAL_SORT]
      ? this.rawData[TABLE_INITIAL_SORT]
      : undefined;
  }

  getAdditionalSortModel() {
    return this.dataGridOptions && this.dataGridOptions[ADDITIONAL_SORT_MODEL]
      ? this.dataGridOptions[ADDITIONAL_SORT_MODEL]
      : undefined;
  }

  getTitleColumn(viewId = 'document') {
    return this.getColumnByKey(this.getTitleColumnId(viewId));
  }

  getTitleColumnId(viewId = 'document') {
    const view = this.findView(viewId);
    if (view && view.getTitleColumn && view.getTitleColumn()) {
      return view.getTitleColumn();
    }
    return this.rawData ? this.rawData[TABLE_TITLE_COLUMN] : undefined;
  }

  getViews() {
    return this.views;
  }

  getColumnsByView(viewId = 'document', excludeTitleColumn = true) {
    const columns = TableConfig.filterColumnsByView(
      this.getColumns(),
      this.getViews(),
      viewId
    );
    return excludeTitleColumn
      ? TableConfig.withoutTitleColumn(columns, this.getTitleColumnId(viewId))
      : columns;
  }

  getDataGridOptions() {
    return this.dataGridOptions;
  }

  getSelectedRowIdDataPath() {
    return (
      this.getDataGridOptions() &&
      this.getDataGridOptions()[GRID_OPTIONS_SELECTED_ROW_ID_DATA_PATH]
    );
  }

  getDefaultPagination() {
    return (
      this.dataGridOptions && this.dataGridOptions[TABLE_DEFAULT_PAGINATION]
    );
  }

  getFilterButtons() {
    return this.filterButtons;
  }

  getValidFilterButtons() {
    return this.validFilterButtons;
  }

  getLayout() {
    return this.layout;
  }

  getLegend() {
    return this.legend;
  }

  getTableType() {
    return this.tableType;
  }

  getColumn(id) {
    return find(this.columns, (column) => column.getId() === id);
  }

  getColumnByKey(key) {
    return find(this.columns, (column) => column.getKey() === key);
  }
}

export default {TableConfig};
